import axios from 'axios';
import { auth } from './firebase';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If running in production (deployed), use relative path
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  // If REACT_APP_API_URL is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Default to localhost for development
  return 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API methods
export const apiService = {
  // Health check
  healthCheck: () => api.get('/health'),

  // Cases
  getCases: () => api.get('/cases'),
  getCase: (id) => api.get(`/cases/${id}`),
  createCase: (data) => api.post('/cases', data),
  updateCase: (id, data) => api.put(`/cases/${id}`, data),
  deleteCase: (id) => api.delete(`/cases/${id}`),

  // Documents
  getCaseDocuments: (caseId) => api.get(`/cases/${caseId}/documents`),
  getDocumentViewUrl: (docId) => api.get(`/documents/${docId}/view-url`),
  uploadDocument: (caseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/cases/${caseId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDocument: (docId) => api.delete(`/documents/${docId}`),

  // AI Analysis
  analyzeDocument: (documentId, analysisType, customPrompt = null) => 
    api.post('/ai/analyze-document', { document_id: documentId, analysis_type: analysisType, custom_prompt: customPrompt }),
  analyzeCase: (caseId, query) => 
    api.post('/ai/case-analysis', { case_id: caseId, query }),
  chatWithAI: (caseId, message, documentIds = null) => 
    api.post('/ai/chat', { case_id: caseId, message, document_ids: documentIds }),
  getCaseAnalyses: (caseId) => api.get(`/cases/${caseId}/analyses`),
  saveAnalysis: (analysisData) => api.post('/analyses/save', analysisData),
  
  // Unified Analysis (NEW - handles all analysis types)
  analyzeUnified: (documentId = null, caseId = null, analysisType = 'standard', customPrompt = null, referenceDocuments = []) =>
    api.post('/analyze-unified', {
      document_id: documentId,
      case_id: caseId,
      analysis_type: analysisType,
      custom_prompt: customPrompt,
      reference_documents: referenceDocuments
    }),
  
  // Streaming Unified Analysis (NEW - real-time streaming)
  analyzeUnifiedStream: async (documentId = null, caseId = null, analysisType = 'standard', customPrompt = null, referenceDocuments = [], onChunk) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    const token = await user.getIdToken();
    const response = await fetch(`${API_BASE_URL}/analyze-unified-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        document_id: documentId,
        case_id: caseId,
        analysis_type: analysisType,
        custom_prompt: customPrompt,
        reference_documents: referenceDocuments
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.substring(6));
            onChunk(data);
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }
  },
  
  // Constitutional Analysis
  constitutionalQuery: (question) => 
    api.post('/ai/constitutional-query', { question }),
  constitutionalAnalysis: (documentId = null, caseId = null, analysisType = 'summary', customPrompt = null) => 
    api.post('/ai/constitutional-analysis', { 
      document_id: documentId, 
      case_id: caseId,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),

  // Bare Acts Analysis
  bareActsQuery: (question) => 
    api.post('/ai/bare-acts-query', { question }),
  bareActsAnalysis: (documentId = null, caseId = null, analysisType = 'summary', customPrompt = null) => 
    api.post('/ai/bare-acts-analysis', { 
      document_id: documentId, 
      case_id: caseId,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),
  
  // Bare Acts Individual RAG API
  getBareActsList: () => api.get('/bare-acts/list'),
  getBareActsStatus: () => api.get('/bare-acts/status'),
  trainBareAct: (actName, forceRetrain = false) => 
    api.post('/bare-acts/train', { act_name: actName, force_retrain: forceRetrain }),
  analyzeWithSpecificActs: (documentId = null, caseId = null, actNames = [], analysisType = 'summary', customPrompt = null) =>
    api.post('/bare-acts/analyze-with-acts', {
      document_id: documentId,
      case_id: caseId,
      act_names: actNames,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),

  // Criminal Law Analysis API
  getCriminalLawsList: () => api.get('/criminal-law/list'),
  analyzeWithCriminalLaw: (documentId = null, caseId = null, lawNames = [], analysisType = 'summary', customPrompt = null) =>
    api.post('/criminal-law/analyze', {
      document_id: documentId,
      case_id: caseId,
      law_names: lawNames,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),

  // Civil Law Analysis API
  getCivilLawsList: () => api.get('/civil-law/list'),
  analyzeWithCivilLaw: (documentId = null, caseId = null, lawNames = [], analysisType = 'summary', customPrompt = null) =>
    api.post('/civil-law/analyze', {
      document_id: documentId,
      case_id: caseId,
      law_names: lawNames,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),

  // Family Law Analysis API
  getFamilyLawsList: () => api.get('/family-law/list'),
  analyzeWithFamilyLaw: (documentId = null, caseId = null, lawNames = [], analysisType = 'summary', customPrompt = null) =>
    api.post('/family-law/analyze', {
      document_id: documentId,
      case_id: caseId,
      law_names: lawNames,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),

  // Analysis History
  getDocumentAnalyses: (documentId) => api.get(`/documents/${documentId}/analyses`),
  getCaseConstitutionalAnalyses: (caseId) => api.get(`/cases/${caseId}/constitutional-analyses`),
  getCaseBareActsAnalyses: (caseId) => api.get(`/cases/${caseId}/bare-acts-analyses`),
  getChatHistory: (caseId) => api.get(`/chat-history/${caseId}`),

  // Research Chat
  sendResearchChat: (data) => api.post('/ai/research-chat', data),
  getResearchHistory: (caseId) => api.get(`/cases/${caseId}/research-history`),

  // Research Vault
  getResearchDocuments: (collection, search) => {
    const params = new URLSearchParams();
    if (collection) params.append('collection', collection);
    if (search) params.append('search', search);
    return api.get(`/research/documents?${params.toString()}`);
  },
  createResearchDocument: (data) => api.post('/research/documents', data),
  updateResearchDocument: (docId, data) => api.put(`/research/documents/${docId}`, data),
  deleteResearchDocument: (docId) => api.delete(`/research/documents/${docId}`),
  getCollections: () => api.get('/research/collections'),

  // Team Management
  getTeamMembers: () => api.get('/team/members'),
  getTeamMember: (memberId) => api.get(`/team/members/${memberId}`),
  createTeamMember: (data) => api.post('/team/members', data),
  updateTeamMember: (memberId, data) => api.put(`/team/members/${memberId}`, data),
  deleteTeamMember: (memberId) => api.delete(`/team/members/${memberId}`),
  getTeamActivity: () => api.get('/team/activity'),

  // Document Export
  exportDocumentPDF: (text) => api.post('/ai/export-pdf', { text }, { responseType: 'blob' }),
  exportDocumentWord: (text) => api.post('/ai/export-word', { text }, { responseType: 'blob' }),

  // Case Schedules
  getCaseSchedules: (caseId) => api.get(`/cases/${caseId}/schedules`),
  createSchedule: (caseId, data) => api.post(`/cases/${caseId}/schedules`, data),
  updateSchedule: (scheduleId, data) => api.put(`/schedules/${scheduleId}`, data),
  deleteSchedule: (scheduleId) => api.delete(`/schedules/${scheduleId}`),

  // Custom Models
  getCustomCategories: () => api.get('/custom-models/categories'),
  createCustomCategory: (data) => api.post('/custom-models/categories', data),
  updateCustomCategory: (categoryId, data) => api.put(`/custom-models/categories/${categoryId}`, data),
  deleteCustomCategory: (categoryId) => api.delete(`/custom-models/categories/${categoryId}`),
  getCategoryDocuments: (categoryId) => api.get(`/custom-models/categories/${categoryId}/documents`),
  uploadCustomDocument: (categoryId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/custom-models/categories/${categoryId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteCustomDocument: (documentId) => api.delete(`/custom-models/documents/${documentId}`),
  trainCustomModel: (categoryId, documentIds) => 
    api.post(`/custom-models/categories/${categoryId}/train`, { document_ids: documentIds }),
  queryCustomModel: (categoryId, question) => 
    api.post(`/custom-models/${categoryId}/query`, { question }),
  analyzeWithCustomModel: (categoryId, documentId = null, caseId = null, analysisType = 'summary', customPrompt = null) => 
    api.post(`/custom-models/${categoryId}/analyze`, {
      document_id: documentId,
      case_id: caseId,
      analysis_type: analysisType,
      custom_prompt: customPrompt
    }),
  getCaseCustomAnalyses: (caseId) => api.get(`/cases/${caseId}/custom-analyses`),

  // Chat Sessions
  sendChatMessage: (caseId, message, documentIds = [], analysisType = 'standard', chatSessionId = null, previousAnalysis = '') =>
    api.post('/ai/chat', {
      case_id: caseId,
      message,
      document_ids: documentIds,
      analysis_type: analysisType,
      chat_session_id: chatSessionId,
      previous_analysis: previousAnalysis
    }),
  getChatSessions: (caseId) => api.get(`/ai/chat/sessions/${caseId}`),
  getChatSession: (sessionId) => api.get(`/ai/chat/session/${sessionId}`),
  deleteChatSession: (sessionId) => api.delete(`/ai/chat/session/${sessionId}`),

  // Sequencer
  createSequence: (caseId, documentIds) => 
    api.post('/sequence/create', { case_id: caseId, document_ids: documentIds }),
  getCaseSequences: (caseId) => api.get(`/cases/${caseId}/sequences`),
  getSequence: (sequenceId) => api.get(`/sequences/${sequenceId}`),
  deleteSequence: (sequenceId) => api.delete(`/sequences/${sequenceId}`),

  // Performance Reviews
  getPerformanceDocuments: (memberId) => api.get(`/team-members/${memberId}/performance-documents`),
  uploadPerformanceDocument: (memberId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/team-members/${memberId}/performance-documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePerformanceDocument: (documentId) => api.delete(`/performance-documents/${documentId}`),
  generatePerformanceReview: (memberId, documentIds) => 
    api.post(`/team-members/${memberId}/generate-review`, { document_ids: documentIds }),
  getPerformanceReviews: (memberId) => api.get(`/team-members/${memberId}/reviews`),
  deletePerformanceReview: (reviewId) => api.delete(`/performance-reviews/${reviewId}`),
  
  // Precedent Search
  getSupremeCourtCases: () => api.get('/precedents/supreme-court-cases'),
  searchPrecedents: (data) => api.post('/precedents/search', data),
};

export default api;

// Convenience exports for individual API methods
export const getSupremeCourtCases = () => apiService.getSupremeCourtCases().then(res => res.data);
export const searchPrecedents = (data) => apiService.searchPrecedents(data).then(res => res.data);
